import React, { useRef, useState, useCallback, useMemo, useEffect } from "react";
import device from "../../img/Renders_Wizard/render1.3.png";
import { useNavigate } from "react-router-dom";
import "./title.css";
import { useTranslation } from "react-i18next";

export const Title = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [deviceStarWarsAnimation, setDeviceStarWarsAnimation] = useState(false);
  const [elementsInView, setElementsInView] = useState({
    device: false,
    title: false,
    subtitle: false,
    description: false,
    buttons: false,
    techSpecs: false,
  });

  const sectionRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastScrollTimeRef = useRef(0);

  // Optimized initialization
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Optimized device animation trigger
  useEffect(() => {
    const timer = setTimeout(() => setDeviceStarWarsAnimation(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Optimized scroll handler with throttling
  useEffect(() => {
    const handleScroll = () => {
      const now = Date.now();

      // Throttle scroll events to 60fps max
      if (now - lastScrollTimeRef.current < 16) return;
      lastScrollTimeRef.current = now;

      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Optimized Intersection Observer - only observe main sections
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -100px 0px",
    };

    const observerCallback = (entries: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting) {
          const elementName = entry.target.dataset.element;
          setElementsInView(prev => ({
            ...prev,
            [elementName]: true,
          }));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Only observe main sections, not decorative elements
    const sections = ['device', 'title', 'subtitle', 'description', 'buttons', 'techSpecs'];
    sections.forEach(name => {
      const element = document.querySelector(`[data-element="${name}"]`);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  // Optimized scroll-based calculations
  const scrollProgress = useMemo(() => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const maxScroll = Math.max(documentHeight - windowHeight, 1);
    return Math.min(scrollY / maxScroll, 1);
  }, [scrollY]);

  // Optimized transform calculations
  const getScrollTransform = useCallback((speed = 0.5, direction = 1) => {
    return scrollY * speed * direction;
  }, [scrollY]);

  // Optimized element transform with memoization
  const elementTransforms: any = useMemo(() => {
    const baseTransforms = {
      device: getScrollTransform(0.05),
      title: getScrollTransform(0.04),
      subtitle: getScrollTransform(0.03),
      description: getScrollTransform(0.02),
      buttons: getScrollTransform(0.01),
    };

    return Object.keys(baseTransforms).reduce((acc, key) => {
      //@ts-ignore
      const isInView = elementsInView[key];
      //@ts-ignore
      acc[key] = {
        //@ts-ignore
        transform: `translateY(${isInView ? baseTransforms[key] : 30}px)`,
        opacity: isInView ? 1 : 0,
        transition: 'all 0.6s ease-out',
      };
      return acc;
    }, {});
  }, [elementsInView, getScrollTransform]);

  // Optimized background video style
  const videoStyle = useMemo(() => ({
    transform: `translateY(${getScrollTransform(0.3)}px)`,
  }), [getScrollTransform]);

  // Optimized overlay style
  const overlayStyle = useMemo(() => ({
    background: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6))`,
  }), []);

  // Optimized star elements - limited count
  const starElements = useMemo(() => {
    return Array.from({ length: 50 }, (_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * 30 + 10;

      return (
        <div
          key={i}
          className="absolute w-px h-px bg-white rounded-full"
          style={{
            left: `${50 + Math.cos(angle) * distance}%`,
            top: `${50 + Math.sin(angle) * distance}%`,
            opacity: 0.7,
            transform: `translate(${scrollProgress * (Math.random() * 10 - 5)}px, ${scrollProgress * (Math.random() * 15 - 7.5)
              }px)`,
          }}
        />
      );
    });
  }, [scrollProgress]);

  // Optimized floating particles - limited count
  const particleElements = useMemo(() => {
    return Array.from({ length: 10 }, (_, i) => (
      <div
        key={i}
        className="absolute w-2 h-2 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full opacity-20 animate-bounce"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 5}s`,
          animationDuration: `${4 + Math.random() * 6}s`,
        }}
      />
    ));
  }, []);

  // Optimized device transform
  const deviceTransform = useMemo(() => {
    if (!deviceStarWarsAnimation) {
      return {
        transform: 'perspective(1000px) translateZ(0) rotateX(0deg) rotateY(0deg) scale(1)',
        transition: 'all 1s ease-out',
      };
    }

    return {
      transform: 'perspective(1000px) translateZ(0) rotateX(0deg) rotateY(0deg) scale(1)',
      transition: 'all 0.3s ease-out',
    };
  }, [deviceStarWarsAnimation]);

  // Optimized button transforms
  const buttonTransform = useMemo(() => ({
    transform: `translateY(${scrollProgress * -2}px) scale(${1 + scrollProgress * 0.02})`,
  }), [scrollProgress]);

  // Clean up animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={sectionRef}
      className="relative min-h-screen w-full overflow-hidden bg-cover bg-center"
    // style={{ backgroundImage: `url(${background})` }}
    >
      {/* Background Video - simplified */}
      <div className="absolute inset-0 overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="https://videos.pexels.com/video-files/2657261/2657261-uhd_2560_1440_24fps.mp4"
          style={videoStyle}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        />

        {/* Simplified overlay */}
        <div className="absolute inset-0" style={overlayStyle} />

        {/* Optimized starfield */}
        <div className="absolute inset-0 opacity-70">
          {starElements}
        </div>

        {/* Optimized particles */}
        <div className="absolute inset-0">
          {particleElements}
        </div>
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 flex flex-col justify-center items-center min-h-screen px-4 sm:px-6 pt-16">
        <div className="flex flex-col lg:flex-row justify-center items-center gap-8 lg:gap-12 max-w-6xl mx-auto w-full">
          {/* Device Section */}
          <div
            data-element="device"
            className="relative lg:w-1/2 flex justify-center"
            style={elementTransforms.device}
          >
            <div className="relative">
              {/* Simplified device container */}
              <div
                className="relative transition-all duration-500 hover:scale-105"
                style={deviceTransform}
              >
                <img
                  src={device}
                  className="relative z-10 w-full max-w-md drop-shadow-2xl"
                  alt="Wizard Device"
                  loading="eager"
                />
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="lg:w-1/2 text-center lg:text-left space-y-6">
            {/* Title */}
            <div data-element="title" style={elementTransforms.title}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent leading-tight">
                WIZARD
              </h1>

              <div
                data-element="subtitle"
                className="mt-4 text-lg md:text-xl font-bold text-purple-300"
                style={elementTransforms.subtitle}
              >
                <span className="inline-block overflow-hidden">
                  {t("home.title.title")}
                </span>
              </div>
            </div>

            {/* Description */}
            <div
              data-element="description"
              className="text-gray-300 text-base md:text-lg leading-relaxed max-w-xl"
              style={elementTransforms.description}
            >
              <p>{t("home.title.description")}</p>
            </div>

            {/* Buttons */}
            <div
              data-element="buttons"
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              style={elementTransforms.buttons}
            >
              <button
                onClick={() => navigate("/register-selection")}
                className="group relative rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 px-6 py-3 text-white font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25"
                style={buttonTransform}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{t("home.title.join")}</span>
                  <svg
                    className="w-5 h-5 transition-transform group-hover:translate-x-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </button>

              {/* <button
                onClick={() => navigate("/checkout")}
                className="group rounded-xl border border-purple-400/50 bg-transparent px-6 py-3 text-white font-semibold transition-all duration-300 hover:bg-purple-500/10"
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{t("home.title.buy_now")}</span>
                  <svg
                    className="w-5 h-5 transition-transform group-hover:scale-110"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    />
                  </svg>
                </div>
              </button> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};